import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { buildSelectorModel } from '@/data/active-domain';

/* ----------------------------------------------------------------------------
 * DomainSelector — the shell's active-domain control (Phase 2, Lane 2A).
 *
 * Shows which connected SEO domain the workspace is currently viewing and lets
 * the user switch between them (or the workspace-wide "All domains" lens). The
 * chosen domain is the shared context that drives Aura + every domain-scoped
 * surface; it is shown as "Viewing: {hostname}".
 *
 *  - Zero connected domains  → renders nothing (no empty control).
 *  - Exactly one domain      → a static, non-interactive "Viewing: {host}" label
 *                              (single-domain workspaces stay frictionless —
 *                              no menu, no prompt).
 *  - Many domains            → a dropdown of every domain + "All domains".
 *
 * "All domains" is the workspace-wide lens — it never merges per-domain SEO
 * records; domain-scoped surfaces render a per-domain breakdown (or ask which
 * domain) under it. Two variants: `bar` (TopBar, desktop dropdown) and `drawer`
 * (MobileDrawer, mobile) so it is usable on both.
 *
 * The drawer variant is a compact DISCLOSURE, default collapsed (2026-07-19
 * regression fix): only the active domain shows as a single row; tapping it
 * expands the choices inline and picking one collapses it again. The domain
 * list must never permanently dominate the drawer — Executive OS + engine nav
 * are the drawer's primary content.
 * -------------------------------------------------------------------------- */
export function DomainSelector({ variant = 'bar' }: { variant?: 'bar' | 'drawer' }) {
  const { active, domains, isAllDomains, needsChoice, select, selectAll, error } = useActiveDomain();
  const [open, setOpen] = useState(false);

  const model = buildSelectorModel({ domains, active, isAllDomains, needsChoice });
  if (!model.visible) return null;

  const onPick = (projectId: number) => {
    setOpen(false);
    void select(projectId).catch(() => undefined);
  };
  const onPickAll = () => {
    setOpen(false);
    selectAll();
  };

  // Single-domain: a static label. Informative, never a prompt.
  if (!model.interactive) {
    const label = (
      <>
        <span aria-hidden className="text-text-tertiary">◍</span>
        <span className="text-text-tertiary">Viewing:</span>
        <span className="truncate font-medium text-text-primary">{model.currentLabel}</span>
      </>
    );
    return variant === 'drawer' ? (
      <div className="flex items-center gap-1.5 px-4 py-3 text-sm">{label}</div>
    ) : (
      <div className="hidden items-center gap-1.5 rounded-md px-2 py-1.5 text-sm lg:flex">{label}</div>
    );
  }

  // --- Drawer variant: compact disclosure, DEFAULT COLLAPSED (mobile). -------
  // Collapsed: one row — "Viewing: {active}" (truncated, no standalone tick).
  // Tap → the choices (each domain + "All domains") expand inline; onPick /
  // onPickAll close it again, so the full list is never permanently visible.
  if (variant === 'drawer') {
    return (
      <div className="p-2">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="drawer-domain-options"
          aria-label="Switch active domain"
          className={cn(
            'flex min-h-[44px] w-full items-center gap-1.5 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-hover',
            needsChoice && 'bg-ai-wash/40',
          )}
        >
          <span aria-hidden className={needsChoice ? 'text-ai' : 'text-text-tertiary'}>◍</span>
          <span className="shrink-0 text-text-tertiary">Viewing:</span>
          <span className="min-w-0 flex-1 truncate font-medium text-text-primary">{model.currentLabel}</span>
          <span
            aria-hidden
            className={cn('shrink-0 text-[10px] text-text-tertiary transition-transform duration-fast', open && 'rotate-180')}
          >
            ▾
          </span>
        </button>

        {open && (
          <div id="drawer-domain-options" className="mt-0.5 flex flex-col rounded-md bg-inset/40 p-1">
            {model.options.map((o) => (
              <button
                key={o.projectId}
                onClick={() => onPick(o.projectId)}
                aria-pressed={o.active}
                className={cn(
                  'flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-hover',
                  o.active && 'bg-inset font-medium',
                )}
              >
                <span className="min-w-0 truncate text-text-primary">{o.label}</span>
                {o.active && <span aria-hidden className="shrink-0 text-xs text-ai">✓</span>}
              </button>
            ))}
            {model.showAllOption && (
              <button
                onClick={onPickAll}
                aria-pressed={model.allActive}
                className={cn(
                  'mt-0.5 flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md border-t border-border-subtle px-2 py-2 text-left text-sm hover:bg-hover',
                  model.allActive && 'bg-inset',
                )}
              >
                <span className="text-text-secondary">All domains</span>
                {model.allActive && <span aria-hidden className="shrink-0 text-xs text-ai">✓</span>}
              </button>
            )}
          </div>
        )}
        {needsChoice && (
          <p className="px-2 pt-1 text-[11px] text-text-tertiary">Pick a domain to scope your workspace.</p>
        )}
        {error && <p className="px-2 pt-1 text-[11px] text-danger">{error}</p>}
      </div>
    );
  }

  // --- Bar variant: a compact dropdown (desktop TopBar). ---------------------
  return (
    <div className="relative hidden lg:block">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch active domain"
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-md border px-2 text-sm transition-colors',
          needsChoice
            ? 'border-ai/40 bg-ai-wash/40 text-text-primary'
            : 'border-border-default bg-canvas text-text-secondary hover:border-border-strong',
        )}
      >
        <span aria-hidden className={needsChoice ? 'text-ai' : 'text-text-tertiary'}>◍</span>
        <span className="text-text-tertiary">Viewing:</span>
        <span className="max-w-[160px] truncate font-medium text-text-primary">{model.currentLabel}</span>
        <span aria-hidden className="text-text-tertiary">▾</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute left-0 z-20 mt-1 w-64 rounded-lg border border-border-default bg-surface p-1.5 shadow-lg animate-fade-in"
          >
            <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
              Active domain
            </div>
            {model.options.map((o) => (
              <button
                key={o.projectId}
                role="menuitemradio"
                aria-checked={o.active}
                onClick={() => onPick(o.projectId)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-hover',
                  o.active && 'bg-inset',
                )}
              >
                <span className="truncate text-text-primary">{o.label}</span>
                {o.active && <span aria-hidden className="text-ai">✓</span>}
              </button>
            ))}
            {model.showAllOption && (
              <>
                <div className="my-1 h-px bg-border-subtle" />
                <button
                  role="menuitemradio"
                  aria-checked={model.allActive}
                  onClick={onPickAll}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-hover',
                    model.allActive && 'bg-inset',
                  )}
                >
                  <span className="text-text-secondary">All domains</span>
                  {model.allActive && <span aria-hidden className="text-ai">✓</span>}
                </button>
              </>
            )}
            {error && <p className="px-2 pt-1 text-[11px] text-danger">{error}</p>}
          </div>
        </>
      )}
    </div>
  );
}
